import { type DragEvent, useCallback, useEffect, useState } from 'react';

import { UPLOAD_TILE_DRAG_LABEL, UPLOAD_TILE_IDLE_LABEL, isLeavingDropZone } from './utils';

export default function useImageDropUpload(onUploadImages?: (files: Array<File>) => void, disabled = false) {
  const [isDragActive, setIsDragActive] = useState(false);
  const canUpload = !!onUploadImages && !disabled;
  const isUploadDragActive = canUpload && isDragActive;

  useEffect(() => {
    if (!canUpload) {
      return;
    }

    const handleDragEnd = () => {
      setIsDragActive(false);
    };

    document.addEventListener('dragend', handleDragEnd);

    return () => {
      document.removeEventListener('dragend', handleDragEnd);
    };
  }, [canUpload]);

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (!canUpload) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(true);
    },
    [canUpload],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      if (!canUpload) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!isLeavingDropZone(event)) {
        return;
      }

      setIsDragActive(false);
    },
    [canUpload],
  );

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (!canUpload) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [canUpload],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (!canUpload) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsDragActive(false);

      const files = Array.from(event.dataTransfer.files ?? []);

      if (files.length > 0) {
        onUploadImages(files);
      }
    },
    [canUpload, onUploadImages],
  );

  return {
    isDragActive: isUploadDragActive,
    uploadLabel: isUploadDragActive ? UPLOAD_TILE_DRAG_LABEL : UPLOAD_TILE_IDLE_LABEL,
    dragDropProps: canUpload
      ? {
          onDragEnter: handleDragEnter,
          onDragLeave: handleDragLeave,
          onDragOver: handleDragOver,
          onDrop: handleDrop,
        }
      : {},
  };
}
